import ApiRequest from "core/api/request";
import JsonStorage from "core/utils/JsonStorage";
import gaEvent from 'core/utils/gaEvent';
import HeapUtil from 'core/utils/HeapUtil';
import { parseTrackingParams } from 'core/utils';

/**
 * Toggle to disable the event tracker.
 * This is independent of serving recommendations
 * May be nice to turn off if Amazon Kinesis dies again...
 */
const ENABLE_TRACKER = true;
const REC_IMPRESSIONS_KEY = 'REC_IMPRESSIONS';
const MAX_REC_HISTORY = 19;

/**
 * Should attempt to send tracker events?
 * @return {bool} True, if tracker enabled
 */
function shouldSend() {
	if (!ENABLE_TRACKER) return false;

	return true;
}

/**
 * Saves an ordered list of viewed recommended listingIds with newest first
 * Used later in funnel (checkout confirmation) to fire a `conversion` event
 *
 * @param listingId id of listing viewed as result of recommendation
 */
function saveToStorage(listingId) {
	// Impression from recs, add to localStorage
	const recStorage = new JsonStorage(REC_IMPRESSIONS_KEY);
	const recListingIds = recStorage.get() || [];

	const idx = recListingIds.indexOf(listingId);
	if (idx > -1) {
		// For pre-existing, remove from list so it gets added to front
		recListingIds.splice(idx, 1);
	}

	if (recListingIds.length >= MAX_REC_HISTORY) {
		// Keep only newest (up to max) so we don't bloat storage
		recListingIds.splice(MAX_REC_HISTORY - 1);
	}

	// Add to front of list
	recListingIds.unshift(listingId);
	recStorage.save(recListingIds);
}

/**
 * Send this event to Heap as well
 */
function sendToHeap(eventName, data = {}) {
	HeapUtil.track(eventName, { ...data });
}

/**
 * Handle impression for a recommendation
 *
 * @param {array} rec - { strategy, listingVersion: { listingId } }
 * @param {string} fallbackStrategy - strategy requested
 * @param {string} campaign - for tracking
 */
export function handleImpression(rec, fallbackStrategy = "fallback", campaign = "") {
	// Fire off impression stat calls, unconcerned with responses
	const listingId = rec?.listingVersion?.listingId;
	if (!listingId) return;
	const data = {
		listingId: listingId,
		engagementType: "impression",
		strategy: rec?.strategy || fallbackStrategy,
		medium: "on_site",
		campaign: campaign,
	};
	sendToHeap("rec_impression", data);
	return ApiRequest.post('/recstat/evt/', data);
}

/**
 * Determine if listingId was a recommended listing (via localStorage)
 *
 * @param listingId id of listing viewed as result of recommendation
 * @return {bool} True, if exists in localStorage. False, otherwise.
 */
export function isRecommendedListing(listingId) {
	if (!listingId) return false;

	const recStorage = new JsonStorage(REC_IMPRESSIONS_KEY);
	const recListingIds = recStorage.get() || [];
	const idx = recListingIds.indexOf(listingId);

	// true if found, false otherwise
	return (idx > -1);
}

/**
 * Send event to the AWS Personalize Event Tracker
 *
 * @param eventName (str) should be in personalize_lib.event_types
 * @param itemId id of listing or category (subject of recommendation)
 * @param impressions (str) comma-separated list of itemIds that were visible to a user when they interacted with itemId
 * @param eventValue (float) order total, for example on a purchase event
 * @return {Promise}
 */
export function sendEvent(eventName, itemId, impressions, eventValue) {
	return new Promise(function(resolve, reject) {
		if (!shouldSend()) {
			// quick bail if not logged in
			console.debug("Personalize tracker event disabled & skipped:", eventName, itemId, impressions, eventValue);
			return resolve(true);
		}

		const body = {
			item_id: itemId,
			event_type: eventName,
		};

		if (eventValue) {
			body['event_value'] = eventValue;
		}

		if (impressions) {
			body['impressions'] = impressions;
		}

		console.debug("Personalize tracker event", body);
		ApiRequest.post('/personalize/events/', body)
			.then(function(res) {
				// Resolve quietly
				// Assume it worked, if it didn't ... oh well
				resolve(true);
			})
			// Dont throw here — just reject
			// Caller should be able to catch & handle as they see fit
			.catch(reject);
	});
}

/**
 * Handle recommendation-releated events for listing view
 *
 * @param listingId id of listing viewed
 */
export function onListingView(listingId, categories=[]) {
	const searchParams = new URLSearchParams(window.location.search);
	const impressions = searchParams.get("impressions");

	// Trigger Personalize Events (used during rec training)
	sendEvent('View', listingId, impressions);

	// Handle personalize ViewFromCategory event:
	const cjtParams = parseTrackingParams();
	const viewFromCategory = cjtParams.pt == "category";
	if (viewFromCategory) {
		const catSlug = cjtParams.gs;
		const catId = categories.find(c => c.slug === catSlug)?.id;
		if (catId) {
			sendEvent('ViewFromCategory', catId, impressions);
		}
	}

	// Record rec stats (possibly)
	onRecsView(listingId);
}

/**
 * Determine if the page view is from a recommended listing (based on query params)
 * If yes, record recommendation stats & save view in localStorage
 *
 * @param listingId id of listing viewed as result of recommendation
 */
export function onRecsView(listingId) {
	const searchParams = new URLSearchParams(window.location.search);
	// Email Recommendation params as defined in Braze: `utm_medium=email&utm_content=recommendation&strategy=<strategy>`
	const recFromEmail = (searchParams.get("utm_medium") === "email" && searchParams.get("utm_content") === "recommendation");
	// On-site Recommendation param as definied in `Recs` component: `sn=recs&strategy=<strategy>`
	// e.g. ?sn=recs&strategy=aws_user_personalization
	const cjtParams = parseTrackingParams();
	const onSiteRecHit = cjtParams.sn == "recs";
	const strategy = searchParams.get("strategy") || "fallback";

	if (!onSiteRecHit && !recFromEmail) {
		// Not from recs, g'bye!
		return;
	}

	// Yes, recommendation! Save to localStorage & record stats:
	saveToStorage(listingId);

	const body = {
		listingId,
		engagementType: "click",
		strategy: strategy,
		medium: recFromEmail ? "email" : "on_site",
		campaign: searchParams.get("utm_campaign") || "",
	}

	sendToHeap("rec_click", body);

	// Record in GA:
	gaEvent('recommendations', 'recommendation_pageview', strategy);

	// Assume it worked, if it didn't ... oh well
	return ApiRequest.post('/recstat/evt/', body);
}

/**
 * Determine if coming from a recommended listing (based on localStorage).
 * If yes, record recommendation stat
 *
 * @param listingId id of listing being added to cart
 */
export function onAddToCart(listingId) {
	if (!listingId) return;

	if (!isRecommendedListing(listingId)) return;

	const searchParams = new URLSearchParams(window.location.search);
	const strategy = searchParams.get("strategy") || "fallback";

	const body = {
		listingId,
		engagementType: "add_to_cart",
		strategy: strategy,
		medium: "on_site",
	};
	sendToHeap("rec_add_to_cart", body);
	// Assume it worked, if it didn't ... oh well
	return ApiRequest.post('/recstat/evt/', body);
}

/**
 * Record purchase event.
 * No need to send along to Heap as conversion event adds an `isRecommendation` attribute
 * ^ that happens in checkout > confirmation
 *
 * @param listingId id of listing purchased
 * @param value {Number} total value of purchase
 */
export function onPurchase(listingId, value = 0) {
	if (!listingId) return;

	const body = {
		listingId,
		engagementType: "purchase",
		strategy: "fallback", // not relevant for this engagementType
		medium: "on_site",
		value: value,
	}
	// Assume it worked, if it didn't ... oh well
	ApiRequest.post('/recstat/evt/', body);

	removeRecFromStorage(listingId);
}

/**
 * Remove listingId from rec impressions in storage, if it exists
 *
 * @param listingId id of listing to remove from list
 */
function removeRecFromStorage(listingId) {
	if (!listingId) return false;

	const recStorage = new JsonStorage(REC_IMPRESSIONS_KEY);
	const recListingIds = recStorage.get() || [];
	const idx = recListingIds.indexOf(listingId);

	if (idx > -1) {
		// Remove from list
		recListingIds.splice(idx, 1);
		recStorage.save(recListingIds);
	}
}

export default {
	handleImpression,
	isRecommendedListing,
	onAddToCart,
	onListingView,
	onPurchase,
	sendEvent,
}
